<script setup>
import {onMounted, ref} from "vue";
import IconSupportHover from "@/components/Icons/IconSupportHover.vue";
import IconSupportActive from "@/components/Icons/IconSupportActive.vue";
import IconSupportDefault from "@/components/Icons/IconSupportDefault.vue";
import SupportForm from "./SupportForm.vue";
import SupportStatusScreen from "./SupportStatusScreen.vue";
import { useSupportForm } from "./useSupportForm";
import { SUPPORT_STATUS } from "./constants";
import {useLK} from "@/controllerLK/LKstore";
import { t } from "@/controllerLK/AvalLocalization";

const isTriggerHover = ref(false);
const isOpen = ref(false);

const LKStore = useLK();

const {
  supportForm,
  submitStatus,
  isFormValid,
  submitForm,
  resetStatus,
} = useSupportForm();

onMounted(() => {
  const { user } = LKStore;

  let fullName = "";
  if (user.firstName) fullName += user.firstName;
  if (user.lastName) fullName += (fullName ? " " : "") + user.lastName;

  supportForm.value.name = fullName;

  if (user.email) supportForm.value.email = user.email;
});

const togglePanel = () => {
  isOpen.value = !isOpen.value;
  if (!isOpen.value) {
    resetStatus();
  }
};

const closePanel = () => {
  isOpen.value = false;
  resetStatus();
};
</script>

<template>
  <div class="support-widget">
    <!-- Кнопка-триггер -->
    <button
      class="support-widget__trigger"
      :class="{ 'support-widget__trigger--active': isOpen }"
      :aria-expanded="isOpen"
      @click="togglePanel"
      @mouseenter="isTriggerHover = true"
      @mouseleave="isTriggerHover = false"
    >
      <IconSupportActive v-if="isOpen" />
      <template v-else>
        <IconSupportHover v-if="isTriggerHover" />
        <IconSupportDefault v-else />
      </template>
    </button>

    <!-- Панель поддержки -->
    <div
      v-if="isOpen"
      class="support-widget__panel support-panel"
      role="dialog"
      aria-modal="true"
    >
      <!-- Шапка -->
      <div class="support-panel__header">
        <h3
          id="support-panel-title"
          class="support-panel__title"
        >
          {{ t("support_widget.title") }}
        </h3>
        <button
          class="support-panel__button support-panel__button--close"
          @click="closePanel"
        >
          <IconCross class="support-panel__icon support-panel__icon--close" />
        </button>
      </div>

      <!-- Контент -->
      <div class="support-panel__content">
        <!-- Форма -->
        <SupportForm
          v-if="submitStatus === SUPPORT_STATUS.IDLE || submitStatus === SUPPORT_STATUS.LOADING"
          v-model:form="supportForm"
          :is-valid="isFormValid"
          @submit="submitForm"
        />

        <!-- Экраны статусов -->
        <SupportStatusScreen
          v-else
          :status="submitStatus"
          @retry="resetStatus"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.support-widget {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 998;

  &__trigger {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    border: none;
    background: transparent;
    padding: 0;
  }

  &__panel {
    position: absolute;
    bottom: 100%;
    right: 6px;
  }
}

.support-panel {
  display: flex;
  flex-direction: column;
  background-color: var(--background-bg-white);
  box-shadow: 1px 2px 10px 0 rgba(19, 30, 39, 0.2);
  border-radius: 10px 10px 2px 10px;
  width: 336px;
  min-height: 536px;
  max-height: 80vh;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 14px;
    border-bottom: 1px solid var(--border-bd-divider);
    flex-shrink: 0;
  }

  &__title {
    margin: 0;
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    color: var(--text-text-main);
  }

  &__button {
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    border: none;
    background-color: transparent;

    &--close {
      width: 24px;
      height: 24px;
      color: var(--icon-icon-secondary);

      &:hover {
        color: var(--icon-icon-on);
      }
    }
  }

  &__icon {
    &--close {
      width: 14px;
      height: 14px;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    flex: 1;
  }
}
</style>